import { execFunctionOnObjectValues } from '../../reducers/reducerHelper';
import type { Asset, Device, TableColumnDetails, Tag } from './types';

const listSeparator = ' ';
const forbiddenChars = [',', '"', "'"];
const assetExportColumnKeys = [
    'name',
    'identification',
    'license_plate',
    'brand',
    'fuel_type',
    'type',
    'status',
    'tags',
    'devices',
] as const;
export type AssetExportColumnKey = (typeof assetExportColumnKeys)[number];
const assetExportColumnKeySet = new Set<string>(assetExportColumnKeys);
const vehicleExportColumnKeys = [
    'type',
    'name',
    'identification',
    'fuel_type',
] as const satisfies readonly AssetExportColumnKey[];

const isAssetExportColumnKey = (columnKey: string): columnKey is AssetExportColumnKey =>
    assetExportColumnKeySet.has(columnKey);

export const getVisibleExportColumnKeys = (columns: TableColumnDetails[]) => {
    const seenColumnKeys = new Set<AssetExportColumnKey>();

    return columns
        .filter(column => column.visible)
        .flatMap(column => (column.key === 'vehicle' ? vehicleExportColumnKeys : [column.key]))
        .filter(isAssetExportColumnKey)
        .filter(columnKey => {
            if (seenColumnKeys.has(columnKey)) {
                return false;
            }

            seenColumnKeys.add(columnKey);
            return true;
        });
};

export function capitalizeFirstLetter(input?: string) {
    return input !== undefined ? input.charAt(0).toUpperCase() + input.slice(1) : '';
}

export function removeForbiddenChars(input: string | null): string {
    if (input) {
        return forbiddenChars.reduce((acc: string, forbiddenChar: string) => replaceAll(acc, forbiddenChar, ''), input);
    } else {
        return '';
    }
}

function replaceAll(str: string, find: string, replace: string) {
    return str.replace(new RegExp(find, 'g'), replace);
}

export function getDevicesForAsset(asset: Asset, devicesFromProps: { [key: string]: Device[] }): string {
    const devices: Device[] = devicesFromProps[asset.id];
    return devices ? devices.map(device => `${device.type}:${device.identification}`).join(listSeparator) : '';
}

export function getDataForCsvLink(assets: Asset[], availableTags: Tag[], devices: { [key: string]: Device[] }) {
    return assets.map(asset => {
        const exportValues: { [key: string]: string | null } = {
            name: asset.name,
            identification: asset.identification,
            license_plate: formatLicensePlateForDisplay(asset),
            brand: asset.brand,
            fuel_type: asset.masterdata?.fuel_type ? capitalizeFirstLetter(asset.masterdata.fuel_type) : '',
            type: capitalizeFirstLetter(asset.type),
            status: capitalizeFirstLetter(asset.status),
            tags: availableTags
                .filter(tag => asset.tags.includes(tag.id))
                .map(tag => tag.name)
                .join(listSeparator),
            devices: getDevicesForAsset(asset, devices),
        };
        return execFunctionOnObjectValues(exportValues, removeForbiddenChars);
    });
}

export const formatLicensePlateForDisplay = ({
    license_plate_country_code: countryCode,
    license_plate: licensePlate,
}: Asset) => {
    const trimmedLicensePlate = licensePlate ? licensePlate.trim() : '';
    const trimmedCountryCode = countryCode ? countryCode.trim() : '';

    return trimmedCountryCode ? `${trimmedLicensePlate} (${trimmedCountryCode})`.trim() : trimmedLicensePlate;
};
